import { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";

import { alignItems, colors, strCode } from "../../../../../../consts";
import { appUserService, t } from "../../../../../../services";
import { dropdownUtil } from "../../../../../../utils";

import {
  DropdownInput,
  FlexContainers,
  Note,
  SmallButton,
  SVG,
  WrapperContainer
} from "../../../../../../components";
import SelectedItemsList from "../../../../../../components/DropdownMultiselectInput/SelectedItemsList";
import { SectionTitle } from "../../../NewFWUCampaignModal/styled";
import { ClickableHeding, ScrolableContent } from "./styled";
import { CurrentFirmwareVersionInput } from "../../../../components/CurrentFirmwareVersionInput/CurrentFirmwareVersionInput";

export const Settings = ({
  fwuCampaignData,
  isToggled,
  disabled,
  setIsToggled = () => {},
  onApplyChanges = () => {}
}) => {
  const [oldMarkets, setOldMarkets] = useState([]);
  const [oldVersions, setOldVersions] = useState([]);

  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [versions, setVersions] = useState([]);

  const markets = useSelector((store) => store.markets?.markets);
  const userMarkets = useSelector((store) => store.configs.userMarkets);

  const isUserHasAllCampaignMarkets = useMemo(() => {
    if (userMarkets && fwuCampaignData) {
      return fwuCampaignData.markets.every((market) => userMarkets.includes(market));
    }

    return false;
  }, [fwuCampaignData, userMarkets]);

  const marketOptions = useMemo(() => {
    return (markets ?? [])?.map((m) => {
      return { id: m.market, value: m.name };
    });
  }, [markets]);

  const isEmptyData = useMemo(() => {
    return !selectedMarkets?.length && !versions?.length;
  }, [selectedMarkets, versions]);

  const getSettingsData = useCallback(() => {
    let newData = {
      selectedMarkets,
      versions,

      oldMarkets,
      oldVersions
    };

    return newData;
  }, [selectedMarkets, versions]);

  useEffect(() => {
    prepopulateData();

    return () => {
      resetFormData();
    };
  }, [fwuCampaignData]);

  const prepopulateData = useCallback(() => {
    if (fwuCampaignData) {
      const oldMarketsList = fwuCampaignData?.markets ?? [];
      const oldMarketsOptions = marketOptions.filter((m) => oldMarketsList.includes(m.id));

      if (!appUserService.isAdmin() && !isUserHasAllCampaignMarkets) {
        oldMarketsList.forEach((market) => {
          if (!oldMarketsOptions.some((option) => option.id === market)) {
            marketOptions.unshift({ id: market, value: market });
          }
        });
      }

      setOldMarkets(oldMarketsOptions);
      setOldVersions(
        (fwuCampaignData?.currentFirmwares ?? []).map((version) => {
          return { id: version, value: version };
        })
      );
    }
  }, [fwuCampaignData, marketOptions]);

  const resetFormData = () => {
    setSelectedMarkets([]);
    setVersions([]);
  };

  const onSave = () => {
    const data = getSettingsData();
    onApplyChanges(data);
  };

  const onCancel = () => {
    resetFormData();
    setIsToggled(false);
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      gap={16}
      width={"100%"}
      height={"100%"}>
      <ClickableHeding onClick={setIsToggled} disabled={disabled}>
        <SectionTitle>{t(strCode.CAMPAIGN_SETTINGS_TITLE)}</SectionTitle>
        <SVG.DownArrowIcon color={colors.RAISIN_100} flipVert={!isToggled} />
      </ClickableHeding>

      {isToggled ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnBetween}
          gap={24}
          width={"100%"}
          height={"calc(100% - 42px)"}>
          <ScrolableContent
            Container={FlexContainers.FlexColumnStart}
            gap={24}
            width={"100%"}
            height={"100%"}>
            <WrapperContainer width={"100%"}>
              <DropdownInput
                id="markets-selector"
                title={t(strCode.SELECT_MARKETS_TITLE)}
                placeholder={t(strCode.SELECT_MARKETS_TITLE)}
                options={marketOptions}
                selectedOptions={[...oldMarkets, ...selectedMarkets] ?? []}
                viewOnlyOptionIds={oldMarkets?.map((m) => m.id)}
                onSelectOption={(id) => {
                  setSelectedMarkets(
                    dropdownUtil.updateSelectedOptions(id, selectedMarkets, marketOptions, true)
                  );
                }}
                InputComponent={
                  [...oldMarkets, ...selectedMarkets]?.length > 0 && (
                    <SelectedItemsList
                      items={[...oldMarkets, ...selectedMarkets]}
                      onDelete={(id) =>
                        setSelectedMarkets(
                          dropdownUtil.updateSelectedOptions(
                            id,
                            selectedMarkets,
                            marketOptions,
                            true
                          )
                        )
                      }
                      checkIsOptionDisabled={(id) => !!oldMarkets.find((m) => m.id === id)}
                      viewOnlyOptionIds={oldMarkets?.map((m) => m.id)}
                    />
                  )
                }
                showCheckboxes
              />
            </WrapperContainer>

            <CurrentFirmwareVersionInput
              versions={[...oldVersions, ...versions]}
              setVersions={setVersions}
              viewOnlyOptionIds={oldVersions?.map((v) => v.id)}
              maxVersion={fwuCampaignData?.versionTo}
              isOptional
            />

            <Note>{t(strCode.FWU_CAMPAIGN_SETTINGS_NOTE)}</Note>
          </ScrolableContent>

          <WrapperContainer Container={FlexContainers.FlexRowEnd} gap={8} width={"100%"}>
            <SmallButton secondary onClick={onCancel}>
              {t(strCode.CANCEL_BTN)}
            </SmallButton>

            <SmallButton onClick={onSave} disabled={isEmptyData}>
              {t(strCode.APPLY_CHANGES_BTN)}
            </SmallButton>
          </WrapperContainer>
        </WrapperContainer>
      ) : null}
    </WrapperContainer>
  );
};

Settings.propTypes = {
  isToggled: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  fwuCampaignData: PropTypes.shape({
    currentFirmwares: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    versionTo: PropTypes.string.isRequired
  }).isRequired,
  setIsToggled: PropTypes.func.isRequired,
  onApplyChanges: PropTypes.func.isRequired
};
